<template>
    <el-dialog title="品名配置" :visible.sync="visibleSync" width="1250px" custom-class="pm-config-dialog"
        :close-on-click-modal="false" append-to-body @open="onOpen">
        <div class="config-meta">
            <span class="meta-item"><span class="meta-label">本地父体</span>{{ parent }}</span>
            <span class="meta-item"><span class="meta-label">本地品名</span>{{ pm }}</span>
            <span class="meta-item"><span class="meta-label">店铺</span>{{ shop }}</span>
            <span class="meta-item"><span class="meta-label">MSKU</span>{{ msku || '-' }}</span>
        </div>

        <!-- 从现有品名配置表中搜索模板，仅套用到当前产品操作表单；修改后仍需点击保存。 -->
        <div class="pm-template-apply">
            <div class="pm-template-title"><i class="el-icon-document-copy"></i> 套用已配置品名模板</div>
            <div class="pm-template-controls">
                <el-input v-model="pmTemplateSearch" size="small" clearable
                    placeholder="输入品名，支持模糊查询" prefix-icon="el-icon-search"
                    style="width:300px" @input="searchPmTemplates" />
                <el-select v-model="pmTemplateSelectedKey" size="small" filterable clearable
                    :loading="pmTemplateSearchLoading" :disabled="!pmTemplateOptions.length"
                    placeholder="选择要套用的已配置品名" style="width:520px">
                    <el-option v-for="item in pmTemplateOptions" :key="pmTemplateKey(item)"
                        :label="pmTemplateLabel(item)" :value="pmTemplateKey(item)">
                        <span>{{ item['本地品名'] }}</span>
                        <span style="float:right;color:#909399;font-size:12px;">
                            {{ item['本地父体'] }} / {{ item['店铺'] }}
                        </span>
                    </el-option>
                </el-select>
                <el-button type="success" plain size="small" :disabled="!pmTemplateSelectedKey"
                    @click="applyPmTemplate">应用到当前品名</el-button>
            </div>
            <div class="msku-hint">数据来源：品名配置表（支持跨父体、跨店铺搜索）。应用只改变当前表单，确认修改后点击底部“保存配置”才会写入当前品名。</div>
        </div>

        <!-- 产品操作域与 MSKU 域在同一品名配置窗口中统筹展示，保存接口保持隔离。 -->
        <div class="config-layout">
            <div class="config-left">
                <el-form label-width="90px" size="small">
                    <el-form-item label="产品类型">
                        <el-select v-model="configForm.product_type" filterable clearable placeholder="选择产品类型"
                            style="width:100%">
                            <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                        </el-select>
                        <div class="product-type-source" :class="productTypeSourceClass">
                            <i :class="productTypeSourceIcon"></i>{{ productTypeSourceText }}
                        </div>
                        <div class="msku-hint">未配置时继承父体；品名选择后会覆盖父体。没有父体配置时，也可以直接在这里手动选择。</div>
                    </el-form-item>
                    <el-divider content-position="left">MSKU生成配置</el-divider>
                    <div class="msku-config-section">
                        <div class="msku-hint" style="margin-bottom:10px;">品名级配置可覆盖父体 MSKU 规则；不会改动产品类型、创建字段、移入或移出配置。</div>
                        <el-form-item label="生成模式">
                            <el-radio-group v-model="mskuRule.use_msku_template">
                                <el-radio :label="false">按属性拼接</el-radio>
                                <el-radio :label="true">按模板生成</el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <div v-if="mskuRuleLoadError" class="msku-preview-warn-text">
                            该品名已保存的 MSKU 规则读取失败：{{ mskuRuleLoadError }}。
                            请重新填写下面的规则后保存，否则现有规则不会被更新。
                        </div>
                        <el-form-item v-if="mskuRule.use_msku_template" label="MSKU模板">
                            <el-input v-model="mskuRule.msku_template" placeholder="如：CB-KPW5" />
                        </el-form-item>
                        <template v-else>
                            <el-form-item label="系列"><el-input v-model="mskuRule.series" placeholder="如：KPW5" /></el-form-item>
                            <el-form-item label="款式"><el-input v-model="mskuRule.style" placeholder="如：Classic" /></el-form-item>
                            <el-form-item label="分隔符"><el-input v-model="mskuRule.msku_separator" style="width:120px" /></el-form-item>
                            <el-form-item label="MSKU颜色">
                                <el-input v-model="mskuRule.msku_color" placeholder="配置该品名默认颜色，如：墨绿色" />
                            </el-form-item>
                        </template>
                        <el-form-item label="国家代码">
                            <el-select v-model="mskuRule.country" filterable placeholder="选择 ISO 两位代码" style="width:100%">
                                <el-option v-if="mskuRuleCountryInvalid" :key="mskuRule.country"
                                    :label="mskuRule.country + '（不受支持，请重新选择）'" :value="mskuRule.country" />
                                <el-option v-for="code in countryCodes" :key="code" :label="code" :value="code" />
                            </el-select>
                            <div v-if="mskuRuleCountryInvalid" class="msku-preview-warn-text">
                                已保存的国家代码「{{ mskuRule.country }}」不是受支持的 ISO 两位代码，
                                必须重新选择，否则保存会被拒绝、该品名也无法生成 MSKU。
                            </div>
                        </el-form-item>
                        <el-form-item label="供应商">
                            <el-input v-model="mskuRule.supplier" placeholder="如：JT；可留空并在生成行手动填写" />
                        </el-form-item>
                        <div class="msku-hint">批量生成优先按关联 SKU 读取本地 SKU-ERP 缓存，缓存未命中时补查 ERP 并带出供应商缩写；品名规则和生成行手填可作为覆盖/备用。</div>
                        <div class="msku-hint">上传 SKU颜色模板可覆盖本次生成颜色；确认“下载 Excel 并保存”后才会更新为该品名默认颜色。</div>
                    </div>
                    <el-divider content-position="left">产品操作配置</el-divider>
                    <el-form-item v-if="showWarehouse" label="仓库" required>
                        <el-select v-model="warehouseValue" clearable filterable placeholder="请选择采购仓库" style="width:100%">
                            <el-option v-for="item in warehouses" :key="item.value" :label="item.text" :value="item.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Parent SKU">
                        <el-input v-model="configForm.parent_sku" placeholder="父体 MSKU" />
                    </el-form-item>
                    <el-form-item label="Variation">
                        <el-select v-model="configForm.variation_theme" filterable clearable
                            placeholder="留空则继承父体主题" style="width:100%" @change="onPmThemeChange">
                            <el-option v-for="t in variationThemes" :key="t.value" :label="t.label"
                                :value="t.value" />
                        </el-select>
                        <div class="msku-hint">
                            品名级可覆盖父体主题：留空 = 继承父体
                            <template v-if="parentVariationTheme">（{{ parentVariationTheme }}）</template>
                            <template v-else>（父体未配置）</template>；已选 = 品名单独使用。
                        </div>
                    </el-form-item>
                    <template v-if="variationFields && variationFields.fields && variationFields.fields.length">
                        <el-form-item v-for="f in variationFields.fields" :key="'vf-' + f.field_name"
                            :label="f.title_zh || f.field_name">
                            <el-select v-if="f.enum && f.enum.length"
                                v-model="configForm.variation_field_values[f.field_name]"
                                filterable allow-create default-first-option clearable style="width:100%"
                                :placeholder="f.field_name">
                                <el-option v-for="opt in f.enum" :key="opt" :label="opt" :value="opt" />
                            </el-select>
                            <el-input v-else v-model="configForm.variation_field_values[f.field_name]"
                                :placeholder="f.field_name" />
                        </el-form-item>
                        <div class="msku-hint" style="margin-bottom:8px;">
                            「{{ variationFields.theme }}」主题需要的变体值（品名级，来源：{{
                                variationFields.theme_source || '-' }}）；空值保存时视为继承父体。
                        </div>
                    </template>
                    <el-alert v-if="variationMissingText" :title="variationMissingText"
                        type="warning" :closable="false" show-icon style="margin:6px 0 10px;" />
                    <el-form-item label="创建主标题">
                        <el-input v-model="configForm.create_main_title" placeholder="如: CoBak 6&quot; Case" />
                    </el-form-item>
                    <el-form-item label="创建副标题">
                        <el-input v-model="configForm.create_sub_title" placeholder="副标题基础" />
                    </el-form-item>
                </el-form>
            </div>
            <div class="config-right">
                <el-tabs v-model="activeScene">
                    <el-tab-pane label="创建字段" name="create">
                        <el-form label-width="120px" size="small">
                            <el-form-item v-for="f in createFields" :key="'c-' + f.field_name"
                                :label="f.title_zh || f.field_name">
                                <div v-if="f.field_name === 'item_package_dimensions'" class="dims-row">
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].length"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].width"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <span class="dims-x">×</span>
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].height"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:110px" />
                                    <el-select v-model="configForm.create_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in dimsUnitOptions(f)" :key="u.value" :label="u.label"
                                            :value="u.value" />
                                    </el-select>
                                </div>
                                <div v-else-if="f.field_name === 'item_package_weight'" class="dims-row">
                                    <el-input-number v-model="configForm.create_field_values[f.field_name].value"
                                        :min="0" :precision="2" controls-position="right" size="small" style="width:160px" />
                                    <el-select v-model="configForm.create_field_values[f.field_name].unit" size="small"
                                        style="width:140px" placeholder="单位">
                                        <el-option v-for="u in weightUnitOptions(f)" :key="u.value" :label="u.label"
                                            :value="u.value" />
                                    </el-select>
                                </div>
                                <el-switch v-else-if="isBoolField(f)"
                                    v-model="configForm.create_field_values[f.field_name]"
                                    active-text="Yes" inactive-text="No" />
                                <el-select v-else-if="hasEnum(f)" v-model="configForm.create_field_values[f.field_name]"
                                    filterable allow-create default-first-option clearable style="width:100%"
                                    :placeholder="f.field_name">
                                    <el-option v-for="opt in f.enum" :key="opt" :label="opt" :value="opt" />
                                </el-select>
                                <el-input-number v-else-if="isNumberField(f)"
                                    v-model="configForm.create_field_values[f.field_name]"
                                    controls-position="right" style="width:100%" />
                                <el-input v-else v-model="configForm.create_field_values[f.field_name]"
                                    :placeholder="f.field_name" />
                            </el-form-item>
                            <div v-if="!createFields.length" class="config-empty">该产品类型下暂无可填写的创建字段</div>
                        </el-form>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>

        <span slot="footer">
            <el-button @click="visibleSync = false">取消</el-button>
            <el-button @click="applyParentConfig">应用父体配置</el-button>
            <el-button type="primary" :loading="configSaving" @click="saveConfig">保存品名配置</el-button>
        </span>
    </el-dialog>
</template>

<script>
export default {
    name: 'PmConfigDialog',
    props: {
        visible: { type: Boolean, default: false },
        parent: { type: String, default: '' },
        pm: { type: String, default: '' },
        shop: { type: String, default: '' },
        msku: { type: String, default: '' },
        warehouse: { type: String, default: '' },
        warehouses: { type: Array, default: () => [] },
        showWarehouse: { type: Boolean, default: true },
    },
    data() {
        return {
            configSaving: false,
            activeScene: 'create',
            productTypes: [],
            variationThemes: [],
            variationFields: null,
            configForm: this.blankConfigForm(),
            mskuRule: this.blankMskuRule(),
            mskuRuleSnapshot: '',
            mskuRuleLoadError: '',
            pmFields: { create: [], move_in: [], move_out: [] },
            // 父体公用字段值（父体级字段的继承默认值）
            parentFieldValues: {},
            // 父体变体主题：品名留空时继承该值
            parentVariationTheme: '',
            // 原始产品类型来源，用于在表单里明确显示“品名覆盖/父体继承”。
            pmProductType: '',
            parentProductType: '',
            // 已配置品名模板搜索/套用
            pmTemplateSearch: '',
            pmTemplateOptions: [],
            pmTemplateSelectedKey: '',
            pmTemplateSearchLoading: false,
            pmTemplateSearchTimer: null,
            pmTemplateSearchToken: 0,
        };
    },
    beforeDestroy() {
        if (this.pmTemplateSearchTimer) clearTimeout(this.pmTemplateSearchTimer);
    },
    watch: {
        // 品名弹窗内手动选择/品名配置带出产品类型后，加载对应字段（父体未配置时也能用）
        'configForm.product_type'(pt, oldPt) {
            if (pt && pt !== oldPt) this.loadAllFieldsAndThemes();
        },
    },
    computed: {
        countryCodes() { return ['AE', 'AU', 'BE', 'BR', 'CA', 'DE', 'ES', 'FR', 'IN', 'IT', 'JP', 'MX', 'NL', 'PL', 'SA', 'SE', 'SG', 'TR', 'UK', 'US']; },
        // 历史数据里的非法国家代码会让保存和生成都失败，这里显式提示用户改正。
        mskuRuleCountryInvalid() {
            const code = String(this.mskuRule.country || '').trim().toUpperCase();
            return !!code && !this.countryCodes.includes(code);
        },
        visibleSync: {
            get() { return this.visible; },
            set(v) { this.$emit('update:visible', v); },
        },
        warehouseValue: {
            get() { return this.warehouse; },
            set(v) { this.$emit('update:warehouse', v); },
        },
        productTypeSourceText() {
            const current = String(this.configForm.product_type || '').trim();
            if (!current) return '未配置产品类型';
            if (this.pmProductType && current === this.pmProductType) return '品名配置（当前品名覆盖）';
            if (this.parentProductType && current === this.parentProductType) return '父体配置（当前品名继承）';
            return '当前表单选择（保存后作为品名覆盖）';
        },
        // 当前主题需要的变体字段名（这些字段在左侧「变体字段」区填写，右侧不重复渲染）
        variationFieldNames() {
            const fields = (this.variationFields && this.variationFields.fields) || [];
            return fields.map(f => f.field_name);
        },
        createFields() {
            return (this.pmFields.create || []).filter(
                f => !this.variationFieldNames.includes(f.field_name));
        },
        productTypeSourceClass() {
            if (!this.configForm.product_type) return 'is-missing';
            if (this.pmProductType && this.configForm.product_type === this.pmProductType) return 'is-pm';
            if (this.parentProductType && this.configForm.product_type === this.parentProductType) return 'is-parent';
            return 'is-manual';
        },
        productTypeSourceIcon() {
            if (!this.configForm.product_type) return 'el-icon-warning';
            if (this.pmProductType && this.configForm.product_type === this.pmProductType) return 'el-icon-edit';
            if (this.parentProductType && this.configForm.product_type === this.parentProductType) return 'el-icon-s-home';
            return 'el-icon-edit-outline';
        },
        // 父体主题下尚未补齐的变体值提示；提交相关刊登/移入前必须补齐。
        variationMissingText() {
            const info = this.variationFields;
            if (!info) return '';
            if (info.unresolved_theme) {
                return `变体主题「${info.theme}」未能解析出所需字段，请到父体配置检查主题或产品类型`;
            }
            const missing = info.missing || [];
            if (!info.theme || !missing.length) return '';
            return `该父体主题「${info.theme}」下还需填写变体值：`
                + missing.map(m => m.title_zh || m.field_name).join('、');
        },
    },
    methods: {
        blankConfigForm() {
            return {
                '本地父体': this.parent || '',
                '本地品名': this.pm || '',
                '店铺': this.shop || '',
                product_type: '',
                parent_sku: '', variation_theme: '',
                create_main_title: '', create_sub_title: '',
                create_field_values: {}, move_in_field_values: {}, move_out_field_values: {},
                // 品名级变体值 {字段: 值}，如 {"color": "Clear", "set_name": "..."}
                variation_field_values: {},
            };
        },
        blankMskuRule() {
            return {
                parent: this.parent || '', pm: this.pm || '', shop: this.shop || '',
                use_msku_template: false, msku_template: '', series: '', style: '',
                msku_separator: '-', supplier: '', country: '', msku_color: '',
            };
        },
        onOpen() {
            this.configForm = this.blankConfigForm();
            this.mskuRule = this.blankMskuRule();
            this.mskuRuleSnapshot = this.mskuRuleSnapshotValue();
            this.mskuRuleLoadError = '';
            this.pmFields = { create: [], move_in: [], move_out: [] };
            this.variationFields = null;
            this.variationThemes = [];
            this.pmProductType = '';
            this.parentProductType = '';
            this.parentVariationTheme = '';
            this.activeScene = 'create';
            this.pmTemplateSearch = '';
            this.pmTemplateOptions = [];
            this.pmTemplateSelectedKey = '';
            this.pmTemplateSearchLoading = false;
            this.pmTemplateSearchToken += 1;
            if (this.pmTemplateSearchTimer) {
                clearTimeout(this.pmTemplateSearchTimer);
                this.pmTemplateSearchTimer = null;
            }
            if (!this.productTypes.length) this.fetchProductTypes();
            if (!this.parent || !this.pm || !this.shop) return;
            // 先加载父体配置（继承产品类型/标题），再加载品名自身配置覆盖。
            this.loadParentConfig(() => this.loadPmConfig());
            this.loadMskuRule();
        },
        loadMskuRule() {
            this.$axios.get('/vue/msku-generation/pm-rule', {
                params: { parent: this.parent, pm: this.pm, shop: this.shop },
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    throw new Error((res && res.message) || 'MSKU 生成配置读取失败');
                }
                this.mskuRule = Object.assign(this.blankMskuRule(), res.data, {
                    parent: this.parent, pm: this.pm, shop: this.shop,
                });
                this.mskuRuleSnapshot = this.mskuRuleSnapshotValue();
            }).catch(error => {
                // 读取失败时不能静默留一个空白表单：那会让用户以为规则丢了，
                // 保存时还会用空白值覆盖库里已有的品名规则（例如国家代码非法）。
                const serverMessage = error && error.response && error.response.data
                    && error.response.data.message;
                this.mskuRuleLoadError = serverMessage || (error && error.message)
                    || 'MSKU 生成配置读取失败';
                this.$message.error('MSKU 生成配置读取失败：' + this.mskuRuleLoadError);
            });
        },
        mskuRuleSnapshotValue(rule = this.mskuRule) {
            const source = rule || {};
            return JSON.stringify({
                use_msku_template: !!source.use_msku_template,
                msku_template: source.msku_template || '', series: source.series || '',
                style: source.style || '', msku_separator: source.msku_separator || '-',
                supplier: source.supplier || '', country: source.country || '', msku_color: source.msku_color || '',
            });
        },
        isMskuRuleDirty() {
            return this.mskuRuleSnapshotValue() !== this.mskuRuleSnapshot;
        },
        // 加载父体配置：产品类型/标题默认继承父体（品名自身配置会在此之后覆盖）
        loadParentConfig(cb) {
            this.$axios.get('/vue/vue_product/parent-config', {
                params: { parent: this.parent }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const d = res.data;
                    this.parentProductType = d.product_type || '';
                    this.configForm.product_type = d.product_type || this.configForm.product_type;
                    this.configForm.create_main_title = d.create_main_title || '';
                    this.configForm.create_sub_title = d.create_sub_title || '';
                    // 父体主题仅作为「留空继承」的参考值，不写入品名表单（品名可自行覆盖）。
                    this.parentVariationTheme = d.variation_theme || '';
                    // 父体公用字段值（父体级创建字段的继承默认值）
                    this.parentFieldValues = d.parent_field_values || {};
                    this.loadAllFieldsAndThemes();
                }
                if (typeof cb === 'function') cb();
            }).catch(() => {
                if (typeof cb === 'function') cb();
            });
        },
        loadPmConfig() {
            this.$axios.get('/vue/vue_product/operation-config', {
                params: { parent: this.parent, pm: this.pm, shop: this.shop }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    const d = res.data;
                    this.pmProductType = d.pm_product_type || '';
                    if (d.parent_product_type) this.parentProductType = d.parent_product_type;
                    const prevPt = this.configForm.product_type;
                    Object.keys(this.configForm).forEach(k => {
                        if (d[k] === undefined || d[k] === null || d[k] === '') return;
                        // 变体主题单独处理：存的是「品名自身主题」，不能用继承后的生效值回填，
                        // 否则父体主题会被误物化成品名覆盖。
                        if (k === 'variation_theme') return;
                        // 场景字段值必须合并，不能整表覆盖：loadAllFieldsAndThemes 已按字段
                        // 生成了 item_package_dimensions / item_package_weight 等默认对象，
                        // 整表覆盖会导致这些字段缺失，模板渲染访问 undefined 报错、弹窗卡死。
                        if (k.endsWith('_field_values')) {
                            const cur = this.configForm[k] || {};
                            const src = d[k] || {};
                            Object.keys(src).forEach(fn => {
                                this.$set(cur, fn, src[fn]);
                            });
                        } else {
                            this.configForm[k] = d[k];
                        }
                    });
                    this.configForm.variation_theme = d.pm_variation_theme || '';
                    this.normalizeWeightFields();
                    // 若品名配置带出不同产品类型，需按最终类型重新加载字段（同时补齐缺失默认值）
                    if (this.configForm.product_type && this.configForm.product_type !== prevPt) {
                        this.loadAllFieldsAndThemes();
                    } else if (d.variation_config) {
                        // 变体配置随品名配置一起返回；为空表示该品名/主题无变体字段
                        this.applyVariationConfig(d.variation_config);
                    } else {
                        this.variationFields = null;
                    }
                }
            }).catch(() => { });
        },
        loadAllFieldsAndThemes() {
            const pt = this.configForm.product_type;
            if (!pt) return;
            // 加载全部创建字段（父体级 + 品名级），父体级默认继承父体公用字段值，品名可修改覆盖
            this.$axios.get('/vue/vue_product/operation-fields', {
                params: { product_type: pt, scene: 'create' }
            }).then(res => {
                if (res.code !== 200) return;
                const fields = res.data || [];
                const key = 'create_field_values';
                fields.forEach(f => {
                    if (this.configForm[key][f.field_name] === undefined) {
                        // 父体级字段：未配置品名时直接推荐父体配置的值
                        const level = f.config_level || 'pm';
                        let dv;
                        if (level === 'parent' && this.parentFieldValues &&
                            this.parentFieldValues[f.field_name] !== undefined &&
                            this.parentFieldValues[f.field_name] !== null &&
                            this.parentFieldValues[f.field_name] !== '') {
                            dv = this.parentFieldValues[f.field_name];
                        } else {
                            dv = this.defaultFieldValue(f);
                        }
                        this.$set(this.configForm[key], f.field_name, dv);
                    }
                    if (f.field_name === 'item_package_dimensions') {
                        const fv = this.configForm[key][f.field_name];
                        if (fv && typeof fv === 'object' && fv.unit === undefined) {
                            this.$set(fv, 'unit', 'inches');
                        }
                    }
                });
                this.normalizeWeightFields();
                this.$set(this.pmFields, 'create', fields);
            }).catch(() => { });
            this.fetchVariationThemes();
            this.loadVariationFields();
        },
        // 变体字段统一解析（与后端 resolve_variation_fields 同源）：按当前主题（品名覆盖或继承父体）
        // 解析需要填写的变体字段；主题切换未保存时用 theme 参数即时解析。
        loadVariationFields() {
            if (!this.parent || !this.pm || !this.shop) {
                this.variationFields = null;
                return;
            }
            this.$axios.get('/vue/vue_product/variation-fields', {
                params: {
                    parent: this.parent, pm: this.pm, shop: this.shop, scene: 'create',
                    theme: this.configForm.variation_theme || '',
                }
            }).then(res => {
                if (res.code !== 200) return;
                this.applyVariationConfig(res.data);
            }).catch(() => { this.variationFields = null; });
        },
        // 应用变体配置：渲染字段列表，并把生效值预填进 variation_field_values（已有值不覆盖）
        applyVariationConfig(config) {
            this.variationFields = config || null;
            const fields = (config && config.fields) || [];
            const effective = (config && config.values) || {};
            const key = 'variation_field_values';
            fields.forEach(f => {
                const cur = this.configForm[key][f.field_name];
                if (cur === undefined || cur === null || cur === '') {
                    const prefill = effective[f.field_name];
                    this.$set(this.configForm[key], f.field_name,
                        prefill === undefined || prefill === null ? '' : prefill);
                }
            });
        },
        // 主题变化：即时按新主题解析需要填写的变体字段（未保存也能预览）
        onPmThemeChange() {
            this.loadVariationFields();
        },
        fetchVariationThemes() {
            const pt = this.configForm.product_type;
            if (!pt) {
                this.variationThemes = [];
                return;
            }
            this.$axios.get('/vue/vue_product/variation-themes', {
                params: { product_type: pt }
            }).then(res => {
                if (res.code === 200) this.variationThemes = res.data || [];
            }).catch(() => { this.variationThemes = []; });
        },
        fetchProductTypes() {
            this.$axios.get('/vue/vue_product/product-types').then(res => {
                if (res.code === 200 && res.data) this.productTypes = res.data;
            }).catch(() => { });
        },
        pmTemplateKey(item) {
            return [item['本地父体'] || '', item['本地品名'] || '', item['店铺'] || ''].join('|');
        },
        pmTemplateLabel(item) {
            return (item['本地品名'] || '') + '｜' + (item['本地父体'] || '') + '｜' + (item['店铺'] || '');
        },
        cloneConfigValue(value) {
            if (value === undefined || value === null) return value;
            if (typeof value !== 'object') return value;
            try {
                return JSON.parse(JSON.stringify(value));
            } catch (e) {
                return value;
            }
        },
        // 输入品名后从 product_operation_config 做模糊查询，避免一次性加载整张配置表。
        searchPmTemplates() {
            if (this.pmTemplateSearchTimer) clearTimeout(this.pmTemplateSearchTimer);
            const keyword = (this.pmTemplateSearch || '').trim();
            const token = ++this.pmTemplateSearchToken;
            this.pmTemplateOptions = [];
            this.pmTemplateSelectedKey = '';
            if (!keyword) {
                this.pmTemplateSearchLoading = false;
                return;
            }
            this.pmTemplateSearchTimer = setTimeout(() => {
                this.pmTemplateSearchLoading = true;
                this.$axios.get('/vue/vue_product/operation-config/search', {
                    params: { pm: keyword, limit: 30 }
                }).then(res => {
                    if (token !== this.pmTemplateSearchToken) return;
                    const rows = (res.code === 200 && Array.isArray(res.data)) ? res.data : [];
                    const currentKey = this.pmTemplateKey({
                        '本地父体': this.parent, '本地品名': this.pm, '店铺': this.shop,
                    });
                    this.pmTemplateOptions = rows.filter(item => this.pmTemplateKey(item) !== currentKey);
                }).catch(() => {
                    if (token === this.pmTemplateSearchToken) this.pmTemplateOptions = [];
                }).then(() => {
                    if (token === this.pmTemplateSearchToken) {
                        this.pmTemplateSearchLoading = false;
                        this.pmTemplateSearchTimer = null;
                    }
                });
            }, 250);
        },
        // 将选中的品名配置复制到当前品名表单，身份列始终保留当前父体/品名/店铺。
        applyPmTemplate() {
            const source = this.pmTemplateOptions.find(item =>
                this.pmTemplateKey(item) === this.pmTemplateSelectedKey);
            if (!source) return this.$message.warning('请先搜索并选择一个已配置品名模板');

            const scalarKeys = [
                'product_type', 'parent_sku',
                'create_main_title', 'create_sub_title',
            ];
            scalarKeys.forEach(key => {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    this.$set(this.configForm, key, this.cloneConfigValue(source[key]));
                }
            });
            ['create_field_values', 'move_in_field_values', 'move_out_field_values'].forEach(key => {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    this.$set(this.configForm, key, this.cloneConfigValue(source[key]) || {});
                }
            });
            this.$set(this.configForm, '本地父体', this.parent || '');
            this.$set(this.configForm, '本地品名', this.pm || '');
            this.$set(this.configForm, '店铺', this.shop || '');

            this.normalizeWeightFields();
            if (this.configForm.product_type) this.loadAllFieldsAndThemes();
            this.$message.success('已套用品名模板，可继续修改后保存');
        },
        isBoolField(f) { return f.value_type === 'boolean' || f.type === 'boolean'; },
        hasEnum(f) { return Array.isArray(f.enum) && f.enum.length > 0; },
        isNumberField(f) {
            return ['integer', 'number'].includes(f.value_type) || ['integer', 'number'].includes(f.type);
        },
        defaultFieldValue(f) {
            if (f.field_name === 'item_package_dimensions') {
                const d = f.default_value;
                if (d && typeof d === 'object') {
                    return {
                        length: d.length || 1, width: d.width || 1,
                        height: d.height || 1, unit: d.unit || 'inches',
                    };
                }
                return { length: 1, width: 1, height: 1, unit: 'inches' };
            }
            if (f.field_name === 'item_package_weight') {
                return this.normalizeWeightValue(f.default_value);
            }
            if (f.field_name === 'list_price') {
                const n = Number(f.default_value);
                return isNaN(n) ? 1 : n;
            }
            if (this.isBoolField(f)) {
                const dv = f.default_value;
                if (typeof dv === 'string') return dv.toLowerCase() === 'yes' || dv.toLowerCase() === 'true';
                return !!dv;
            }
            if (f.type === 'array') {
                // array 类型字段：default_value 可能是配置的纯值（如 brand="CoBak"）或空数组模板默认
                const dv = f.default_value;
                if (Array.isArray(dv)) return dv.length ? dv : '';
                return dv || '';
            }
            if (this.isNumberField(f)) {
                const n = Number(f.default_value);
                return isNaN(n) ? 0 : n;
            }
            return f.default_value || '';
        },
        // 重量值归一化为 {value, unit}（兼容历史存量为纯数字的情况）
        normalizeWeightValue(v) {
            if (v && typeof v === 'object') {
                return { value: v.value != null ? v.value : 1, unit: v.unit || 'pounds' };
            }
            const n = Number(v);
            return { value: isNaN(n) ? 1 : n, unit: 'pounds' };
        },
        // 三个场景字段值中的重量统一归一化（旧配置存量为纯数字时兼容）
        normalizeWeightFields() {
            ['create_field_values', 'move_in_field_values', 'move_out_field_values'].forEach(k => {
                const fv = this.configForm[k] || {};
                if (fv.item_package_weight !== undefined && fv.item_package_weight !== null) {
                    this.$set(fv, 'item_package_weight', this.normalizeWeightValue(fv.item_package_weight));
                }
            });
        },
        dimsUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'inches', label: 'inches' },
                { value: 'cm', label: 'cm' },
                { value: 'mm', label: 'mm' },
            ];
        },
        weightUnitOptions(f) {
            if (f.unit_options && f.unit_options.length) return f.unit_options;
            return [
                { value: 'pounds', label: 'Pounds' },
                { value: 'ounces', label: 'Ounces' },
                { value: 'kilograms', label: 'Kilograms' },
                { value: 'grams', label: 'Grams' },
            ];
        },
        saveConfig() {
            if (!this.configForm['本地品名']) return this.$message.warning('缺少本地品名');
            if (this.showWarehouse && !this.warehouse) return this.$message.warning('请选择采购仓库');
            const rule = Object.assign({}, this.mskuRule, {
                parent: this.parent, pm: this.pm, shop: this.shop,
            });
            const saveMskuRule = this.isMskuRuleDirty();
            if (saveMskuRule && !this.validateMskuRule(rule)) return;
            // 产品类型可留空：品名未配置时后端会继承父体配置。
            // MSKU 域有配置时同次保存，但仍由独立接口写入，避免影响产品操作字段。
            this.configSaving = true;
            const configPayload = Object.assign({}, this.configForm);
            // 当前值若只是父体回显且品名原本没有覆盖，继续保留“继承”语义，
            // 避免用户保存其它字段时无意间把父体产品类型复制成品名覆盖。
            if (!this.pmProductType && this.parentProductType
                && configPayload.product_type === this.parentProductType) {
                configPayload.product_type = '';
            }
            const requests = [this.$axios.post('/vue/vue_product/operation-config', configPayload)];
            if (saveMskuRule) requests.push(this.$axios.post('/vue/msku-generation/pm-rule', rule));
            Promise.all(requests).then(results => {
                if (results.every(res => res.code === 200)) {
                    if (saveMskuRule) this.mskuRuleSnapshot = this.mskuRuleSnapshotValue(rule);
                    this.$message.success('品名配置已保存');
                    this.visibleSync = false;
                    this.$emit('saved');
                } else {
                    const failed = results.find(res => res.code !== 200) || {};
                    this.$message.error(failed.message || '保存失败');
                }
            }).catch(e => {
                this.$message.error('保存失败: ' + e);
            }).finally(() => {
                this.configSaving = false;
            });
        },
        validateMskuRule(rule) {
            rule.country = String(rule.country || '').trim().toUpperCase();
            if (!rule.country) {
                this.$message.warning('请填写 MSKU 配置的国家代码');
                return false;
            }
            if (!this.countryCodes.includes(rule.country)) {
                this.$message.warning('国家代码必须为已支持的 ISO 两位英文代码');
                return false;
            }
            if (rule.use_msku_template && !rule.msku_template) {
                this.$message.warning('模板模式必须填写 MSKU模板');
                return false;
            }
            if (!rule.use_msku_template && !rule.msku_color) {
                this.$message.warning('属性模式必须填写 MSKU颜色');
                return false;
            }
            return true;
        },
        // 快速应用父体配置到品名（如从其它父体移入的品名）：用当前父体的父体级字段覆盖品名
        // 表单对应值，品名可修改后保存；不影响父体原有配置。
        applyParentConfig() {
            if (!this.parent) return this.$message.warning('缺少本地父体');
            this.$axios.get('/vue/vue_product/parent-config', {
                params: { parent: this.parent }
            }).then(res => {
                if (res.code !== 200 || !res.data) return this.$message.error('父体配置不存在');
                const d = res.data;
                // 父体级字段值覆盖到品名表单（父体公用字段）
                const pv = d.parent_field_values || {};
                const key = 'create_field_values';
                Object.keys(pv).forEach(fn => {
                    const v = pv[fn];
                    if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
                        this.$set(this.configForm[key], fn, v);
                    }
                });
                // 标量：只应用产品操作域字段；MSKU 区域已自动展示父体继承值，
                // 此处无需复制成品名级覆盖项。变体主题一并物化为品名覆盖值（可再改）。
                [['create_main_title', 'create_main_title'],
                    ['create_sub_title', 'create_sub_title']].forEach(([src, dst]) => {
                    if (!this.configForm[dst] && d[src]) this.configForm[dst] = d[src];
                });
                this.configForm.variation_theme = d.variation_theme || '';
                if (!this.configForm.product_type && d.product_type) {
                    this.configForm.product_type = d.product_type;
                    this.loadAllFieldsAndThemes();
                }
                this.normalizeWeightFields();
                this.loadVariationFields();
                this.$message.success('已应用父体配置（可修改后保存）');
            }).catch(() => this.$message.error('应用父体配置失败'));
        },
    },
};
</script>

<style scoped>
.config-meta { display: flex; flex-wrap: wrap; gap: 8px 28px; font-size: 13px; color: #606266;
    background: #f5f7fa; border-radius: 4px; padding: 10px 14px; margin-bottom: 12px; }
.meta-item { display: inline-flex; align-items: center; }
.meta-label { color: #909399; margin-right: 6px; }
.pm-template-apply { padding: 10px 14px; margin-bottom: 12px; border: 1px solid #d9ecff;
    background: #f4f9ff; border-radius: 4px; }
.pm-template-title { color: #409eff; font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.pm-template-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.config-layout { display: flex; gap: 20px; align-items: flex-start; }
.config-left { width: 440px; flex-shrink: 0; border-right: 1px solid #ebeef5; padding-right: 20px;
    max-height: 560px; overflow-y: auto; }
.config-right { flex: 1; min-width: 0; max-height: 560px; overflow-y: auto; padding-left: 6px; }
.config-right-full { width: 100%; padding-left: 0; }
.config-empty { color: #909399; font-size: 13px; padding: 12px 0; }
.msku-hint { font-size: 12px; color: #909399; line-height: 1.6; }
.product-type-source { font-size: 12px; line-height: 1.6; margin-top: 4px; }
.product-type-source i { margin-right: 4px; }
.product-type-source.is-missing { color: #f56c6c; }
.product-type-source.is-pm,
.product-type-source.is-manual { color: #409eff; }
.product-type-source.is-parent { color: #e6a23c; }
.msku-preview { font-size: 12px; color: #409eff; line-height: 1.6; background: #ecf5ff;
    border: 1px solid #d9ecff; border-radius: 4px; padding: 6px 10px; word-break: break-all; }
.msku-preview-warn { color: #CC0000; background: #fef0f0; border-color: #fbc4c4; font-weight: bold; }
.msku-preview-warn-text { font-size: 12px; color: #CC0000; line-height: 1.6; margin-top: 4px; }
.dims-row { display: flex; align-items: center; gap: 6px; }
.dims-x { color: #909399; }
</style>

<!-- 全局样式：弹窗 append-to-body 渲染在 body 下，scoped 深度选择器无法命中 -->
<style>
/* 仅最外层样式适配：弹窗整体上移 100px（15vh 默认垂直位置基础上再向上偏移），内部结构不变 */
.pm-config-dialog { margin-top: max(0px, calc(15vh - 100px)) !important; }
</style>
